import React, { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { CaretDown, CaretRight, Pencil } from "@phosphor-icons/react";
import { labelFor } from "../lib/enumLabels";
import { useTranslation } from "react-i18next";

/**
 * v0.4.37 (post-D9 enhancement, operator sign-off 2026-06-04).
 *
 * Audit chip on Step 6 surfacing the calc-level
 * `manual_rate_corrections` rollup written by the Task B endpoint.
 * Each entry is one Apply-batch (`POST /lint/apply`); expanding the
 * chip reveals per-row picks + picker identity + ISO timestamp.
 *
 * ADMIN-only visibility — operators don't need this surface during
 * routine filing review.
 */
export const ManualRateCorrectionsChip = ({ corrections }) => {
  const { admin } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (admin?.role !== "ADMIN") return null;
  const list = Array.isArray(corrections) ? corrections : [];
  if (list.length === 0) return null;
  const total = list.reduce((n, b) => n + (b.batch_size || 0), 0);
  return (
    <div
      data-testid="manual-rate-corrections-chip"
      className="border border-amber-500/30 bg-amber-500/5 rounded-xl"
    >
      <button
        type="button"
        data-testid="manual-rate-corrections-toggle"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-500/10 dark:bg-amber-900/40"
      >
        <span className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 font-bold">
          {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
          <Pencil size={12} weight="bold" />
          {t("ui.manual_rate_corrections.title", { total, defaultValue: `Manual rate corrections (${total})` })}
        </span>
        <span className="tabular-nums text-[10px] text-slate-600 dark:text-slate-300">
          {list.length > 1 ? t("ui.manual_rate_corrections.batches", { count: list.length, defaultValue: `${list.length} batches` }) : t("ui.manual_rate_corrections.batch", { count: list.length, defaultValue: `${list.length} batch` })}
        </span>
      </button>
      {open && (
        <div
          data-testid="manual-rate-corrections-list"
          className="border-t border-amber-500/20 px-4 py-3 tabular-nums text-[11px] text-slate-500 dark:text-slate-400 space-y-3"
        >
          {list.map((batch, i) => (
            <div key={i} data-testid={`manual-rate-corrections-batch-${i}`}>
              <div className="text-slate-600 dark:text-slate-300 mb-1">
                <span className="text-amber-700 dark:text-amber-300">{batch.picked_by}</span>
                {" · "}
                <span className="text-slate-500 dark:text-slate-400">{batch.picked_at}</span>
                {" · "}
                <span className="text-slate-500 dark:text-slate-400">{t("ui.manual_rate_corrections.rows", { count: batch.batch_size, defaultValue: `${batch.batch_size} row(s)` })}</span>
              </div>
              <ul className="ml-3 space-y-0.5">
                {(batch.items || []).map((it, j) => (
                  <li key={j} className="text-slate-500 dark:text-slate-400">
                    <span className="text-amber-700 dark:text-amber-300">{it.id}</span>
                    {" · "}{labelFor("tax_category", it.rate_type)} ({it.rate})
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
